import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Plus, MapPin, Clock, Search, Filter, Copy, Trash2, Edit, MoreVertical, Globe, Save } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { COMMON_TIMEZONES, detectUserTimezone, getTimezoneOffset, getCurrentTimeInTimezone } from '../utils/timezones';
import EventDaysEditor from '@/components/EventDaysEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  active: 'bg-blue-100 text-blue-700 border-blue-300',
  executed: 'bg-green-100 text-green-700 border-green-300',
  archived: 'bg-slate-100 text-slate-700 border-slate-300'
};

const STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  executed: 'Executed',
  archived: 'Archived'
};

export default function Events({ user }) {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [detectingTimezone, setDetectingTimezone] = useState(false);
  const [editTimezoneDialog, setEditTimezoneDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    client: '',
    date: '',
    timezone: 'Australia/Sydney',
    start_time: '',
    end_time: '',
    event_type: 'general',
    custom_modalities: [],
    coordinator_contacts: [],
    days: [],
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterAndSortEvents();
  }, [events, searchTerm, statusFilter, sortBy]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortEvents = () => {
    let filtered = [...events];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.client.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }

    // Sort events
    filtered.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.date) - new Date(a.date);
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'status') {
        return a.status.localeCompare(b.status);
      }
      return 0;
    });

    setFilteredEvents(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    // If multi-day editor was used, mirror first day to legacy fields and keep days[].
    if (Array.isArray(payload.days) && payload.days.length > 0) {
      const first = payload.days[0];
      payload.date = first.date;
      payload.start_time = first.start_time;
      payload.end_time = first.end_time;
    } else {
      delete payload.days;
    }
    try {
      await axios.post(`${API}/events`, payload);
      toast.success('Event created successfully!');
      setOpen(false);
      fetchEvents();
      setFormData({
        name: '', client: '', date: '',
        timezone: 'Australia/Sydney',
        start_time: '', end_time: '',
        event_type: 'general',
        custom_modalities: [],
        coordinator_contacts: [],
        days: [],
      });
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleDelete = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"? This will delete all associated plans, spots, and timing data. This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Event deleted successfully ✓', { duration: 2000 });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleDuplicate = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/duplicate`);
      toast.success('Event duplicated successfully ✓', { duration: 2000 });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to duplicate event');
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await axios.put(`${API}/events/${eventId}/status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Event status changed to ${STATUS_LABELS[newStatus]} ✓`, { duration: 2000 });
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleOpenTimezoneEdit = (event) => {
    setEditingEvent(event);
    setSelectedTimezone(event.timezone || 'Australia/Sydney');
    setEditTimezoneDialog(true);
  };

  const handleSaveTimezone = async () => {
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, {
        timezone: selectedTimezone
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Timezone updated successfully ✓', { duration: 2000 });
      setEditTimezoneDialog(false);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update timezone');
      console.error('Error updating timezone:', error);
    }
  };

  const getStatusCounts = () => {
    return {
      all: events.length,
      draft: events.filter(e => e.status === 'draft').length,
      active: events.filter(e => e.status === 'active').length,
      executed: events.filter(e => e.status === 'executed').length,
      archived: events.filter(e => e.status === 'archived').length
    };
  };

  const statusCounts = getStatusCounts();

  // Detect timezone when dialog opens
  const handleDialogOpenChange = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      setDetectingTimezone(true);
      try {
        const detectedTimezone = await detectUserTimezone();
        setFormData(prev => ({ ...prev, timezone: detectedTimezone }));
        console.log('Auto-detected timezone:', detectedTimezone);
      } catch (error) {
        console.error('Failed to detect timezone:', error);
        // Keep default timezone
      } finally {
        setDetectingTimezone(false);
      }
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar user={user} />
      <div className="main-content flex-1 w-full overflow-x-hidden">
        <div className="page-header flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-6">
          <div className="w-full sm:w-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Events</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your photography events and coverage plans</p>
          </div>
          <Dialog open={open} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button className="btn-gradient w-full sm:w-auto min-h-[44px]" data-testid="create-event-dialog-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Event</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="My Photography Event"
                    data-testid="event-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client</Label>
                  <Input
                    required
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    placeholder="Sportograf"
                    data-testid="client-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <select
                    className="w-full border rounded-lg p-2 min-h-[44px]"
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  >
                    <option value="general">General Photography</option>
                    <option value="sports">Sports / Race</option>
                    <option value="wedding">Wedding</option>
                    <option value="real_estate">Real Estate</option>
                    <option value="corporate">Corporate</option>
                    <option value="events">Events / Concerts</option>
                    <option value="fitness">Fitness / Hyrox</option>
                  </select>
                  <p className="text-xs text-gray-500">This determines which scene types and gear suggestions appear when creating spots.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      data-testid="date-input"
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Timezone
                      {detectingTimezone && <span className="text-xs text-gray-500">(detecting...)</span>}
                    </Label>
                    <select
                      className="w-full border rounded-lg p-2 min-h-[44px]"
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                      data-testid="timezone-select"
                      disabled={detectingTimezone}
                    >
                      {COMMON_TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label} - {getTimezoneOffset(tz.value)}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Auto-detected based on your location. You can change it manually.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      required
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      data-testid="start-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      required
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      data-testid="end-time-input"
                    />
                  </div>
                </div>
                <EventDaysEditor
                  days={formData.days || []}
                  onChange={(days) => setFormData({ ...formData, days })}
                  fallback={{ date: formData.date, start_time: formData.start_time, end_time: formData.end_time }}
                />
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-event-btn">
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-gradient" data-testid="create-event-btn">
                    Create Event
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search events by name or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border rounded-lg px-4 py-2 bg-white"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="status">Sort by Status</option>
            </select>
          </div>

          {/* Status Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'all'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Events ({statusCounts.all})
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'draft'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Draft ({statusCounts.draft})
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'active'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active ({statusCounts.active})
            </button>
            <button
              onClick={() => setStatusFilter('executed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'executed'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Executed ({statusCounts.executed})
            </button>
            <button
              onClick={() => setStatusFilter('archived')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                statusFilter === 'archived'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Archived ({statusCounts.archived})
            </button>
          </div>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">
              {searchTerm || statusFilter !== 'all' ? 'No events match your filters' : 'No events yet'}
            </p>
            {(searchTerm || statusFilter !== 'all') && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredEvents.map((event) => (
              <Card key={event.id} className="glass-card p-6 hover:shadow-xl transition-all relative group" data-testid={`event-card-${event.id}`}>
                {/* Action Menu */}
                <div className="absolute top-4 right-4 z-10">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      
                      {/* Status Change Submenu */}
                      <DropdownMenuLabel className="text-xs text-gray-500 font-normal">Change Status</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'draft')}>
                        <span className="w-2 h-2 rounded-full bg-gray-500 mr-2"></span>
                        Draft
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'active')}>
                        <span className="w-2 h-2 rounded-full bg-blue-500 mr-2"></span>
                        Active
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'executed')}>
                        <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
                        Executed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleStatusChange(event.id, 'archived')}>
                        <span className="w-2 h-2 rounded-full bg-slate-500 mr-2"></span>
                        Archived
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem onClick={() => handleOpenTimezoneEdit(event)}>
                        <Globe className="mr-2 h-4 w-4" />
                        Edit Timezone
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem onClick={() => handleDuplicate(event.id)}>
                        <Copy className="mr-2 h-4 w-4" />
                        Duplicate
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem
                        onClick={() => handleDelete(event.id, event.name)}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <Link to={`/events/${event.id}`} className="block">
                  <div className="flex items-start justify-between mb-3">
                    <Calendar className="w-8 h-8 text-emerald-600" />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[event.status]}`}>
                      {STATUS_LABELS[event.status]}
                    </span>
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{event.name}</h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{event.client}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(event.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{event.start_time} - {event.end_time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      <span className="text-xs">{event.timezone || 'Australia/Sydney'}</span>
                    </div>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit Timezone Dialog */}
      <Dialog open={editTimezoneDialog} onOpenChange={setEditTimezoneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Edit Event Timezone
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Event</Label>
              <p className="text-sm font-medium text-gray-700">{editingEvent?.name}</p>
              <p className="text-xs text-gray-500">{editingEvent?.date} • {editingEvent?.start_time} - {editingEvent?.end_time}</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Select Timezone
              </Label>
              <select
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
                className="w-full border rounded-lg p-2 min-h-[44px]"
              >
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label} - {getTimezoneOffset(tz.value)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                Current time in {selectedTimezone}: {getCurrentTimeInTimezone(selectedTimezone)}
              </p>
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setEditTimezoneDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTimezone} className="bg-emerald-600 hover:bg-emerald-700">
              <Save className="w-4 h-4 mr-2" />
              Save Timezone
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
